import { useMemo } from "react";
import type { Bucket, Channel, KeywordRow, ProductRow, SourceRow } from "../../types/analytics";
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  CHANNEL_ORDER,
  int,
  isWeekend,
  metricSortValue,
  pctStr,
  roasStr,
  sumChannels,
  yen,
  type SortKey,
} from "../../lib/analyticsCalc";

export type BreakdownTab = "product" | "source" | "period" | "keyword";
export interface SortState {
  key: SortKey;
  dir: 1 | -1;
}

interface BreakdownProps {
  tab: BreakdownTab;
  onTabChange: (t: BreakdownTab) => void;
  productsTable: ProductRow[];
  sources: SourceRow[];
  buckets: Bucket[];
  keywords: KeywordRow[];
  channels: Record<Channel, boolean>;
  granularity: "day" | "week" | "month";
  sort: SortState;
  onSortChange: (s: SortState) => void;
  selectedProduct: string | null;
  onSelectProduct: (key: string) => void;
}

const TABS: { key: BreakdownTab; label: string }[] = [
  { key: "product", label: "商品別" },
  { key: "source", label: "流入元別" },
  { key: "period", label: "期間推移" },
  { key: "keyword", label: "検索キーワード" },
];

const SORT_COLS: { key: SortKey; label: string }[] = [
  { key: "sales", label: "売上" },
  { key: "sessions", label: "アクセス" },
  { key: "orders", label: "注文" },
  { key: "cvr", label: "転換率" },
  { key: "aov", label: "客単価" },
  { key: "ad", label: "広告費" },
  { key: "roas", label: "ROAS" },
];

export function Breakdown({
  tab,
  onTabChange,
  productsTable,
  sources,
  buckets,
  keywords,
  channels,
  granularity,
  sort,
  onSortChange,
  selectedProduct,
  onSelectProduct,
}: BreakdownProps) {
  const active = useMemo(() => CHANNEL_ORDER.filter((c) => channels[c]), [channels]);

  const hint =
    tab === "product"
      ? "行をクリックでその商品に絞り込み"
      : tab === "source"
      ? "広告流入は広告費・ROASを表示"
      : tab === "period"
      ? `${{ day: "日次", week: "週次", month: "月次" }[granularity]}・新しい順`
      : "楽天のみ・月次累計";

  const sortedProducts = useMemo(() => {
    const rows = productsTable.map((p) => ({ p, m: sumChannels(p.by_channel, active) }));
    rows.sort((a, b) => (metricSortValue(a.m, sort.key) - metricSortValue(b.m, sort.key)) * sort.dir);
    return rows;
  }, [productsTable, active, sort]);

  const visibleSources = useMemo(() => sources.filter((s) => channels[s.channel]), [sources, channels]);
  const sourceMax = visibleSources.length ? Math.max(...visibleSources.map((s) => s.sessions)) || 1 : 1;
  const sourceSessionsTotal = visibleSources.reduce((a, s) => a + s.sessions, 0) || 1;

  const periodRows = useMemo(() => buckets.slice().reverse(), [buckets]);

  const sortArrow = (key: SortKey) => (sort.key === key ? (sort.dir < 0 ? "↓" : "↑") : "");
  const clickSort = (key: SortKey) => onSortChange(sort.key === key ? { key, dir: sort.dir === -1 ? 1 : -1 } : { key, dir: -1 });

  return (
    <section className="an-breakdown-card">
      <div className="an-breakdown-head">
        <div className="an-breakdown-title">ブレークダウン</div>
        <div className="an-seg" role="group" aria-label="ブレークダウン種別">
          {TABS.map((t) => (
            <button key={t.key} className={`an-seg-btn small ${tab === t.key ? "active" : ""}`} onClick={() => onTabChange(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="an-breakdown-hint">{hint}</div>
      </div>

      <div className="an-table-wrap">
        {tab === "product" && (
          <table className="an-table" style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th className="left">商品</th>
                <th className="left" style={{ width: 130 }}>
                  チャネル内訳
                </th>
                {SORT_COLS.map((c) => (
                  <th key={c.key} className="sortable" onClick={() => clickSort(c.key)}>
                    {c.label} {sortArrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map(({ p, m }) => {
                const salesTotal = CHANNEL_ORDER.reduce((a, ch) => a + p.by_channel[ch].sales, 0) || 1;
                const selected = selectedProduct === p.key;
                return (
                  <tr key={p.key} className={`clickable ${selected ? "selected" : ""}`} onClick={() => onSelectProduct(p.key)}>
                    <td className="left">
                      <div className="an-product-name">{p.name}</div>
                      <div className="an-product-sub">
                        {p.skus.join(" / ") || "—"} ・ {p.unit_price != null ? `¥${p.unit_price.toLocaleString("ja-JP")}` : "—"}
                      </div>
                    </td>
                    <td>
                      <div className="an-split-bar">
                        {CHANNEL_ORDER.map((ch) => (
                          <div key={ch} style={{ width: `${(p.by_channel[ch].sales / salesTotal) * 100}%`, background: CHANNEL_COLOR[ch] }} />
                        ))}
                      </div>
                    </td>
                    <td className="an-td-strong">{yen(m.sales)}</td>
                    <td>{m.sessions != null ? int(m.sessions) : "—"}</td>
                    <td>{int(m.orders)}</td>
                    <td>{m.sessions ? pctStr(m.orders, m.sessions) : "—"}</td>
                    <td>{m.orders ? yen(m.sales / m.orders) : "—"}</td>
                    <td>{yen(m.ad_spend)}</td>
                    <td>{roasStr(m.sales, m.ad_spend)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {tab === "source" && (
          <table className="an-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="left">流入元</th>
                <th className="left">チャネル</th>
                <th>アクセス</th>
                <th className="left" style={{ width: 140 }}>
                  構成比
                </th>
                <th>注文</th>
                <th>転換率</th>
                <th>売上</th>
                <th>広告費</th>
                <th>ROAS</th>
              </tr>
            </thead>
            <tbody>
              {visibleSources
                .slice()
                .sort((a, b) => b.sessions - a.sessions)
                .map((s) => (
                  <tr key={`${s.channel}-${s.source}`}>
                    <td className="left">
                      {s.source}
                      {s.is_ad ? "（広告）" : ""}
                    </td>
                    <td className="left">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: CHANNEL_COLOR[s.channel], display: "inline-block" }} />
                        {CHANNEL_LABEL[s.channel]}
                      </span>
                    </td>
                    <td>{int(s.sessions)}</td>
                    <td>
                      <div className="an-share-cell">
                        <div className="an-bar-track">
                          <div className="an-bar-fill" style={{ width: `${(s.sessions / sourceMax) * 100}%`, background: CHANNEL_COLOR[s.channel] }} />
                        </div>
                        <span className="an-share-pct">{pctStr(s.sessions, sourceSessionsTotal, 1)}</span>
                      </div>
                    </td>
                    <td>{s.orders != null ? int(s.orders) : "—"}</td>
                    <td>{s.orders != null ? pctStr(s.orders, s.sessions) : "—"}</td>
                    <td className="an-td-strong">{s.sales != null ? yen(s.sales) : "—"}</td>
                    <td>{s.is_ad && s.ad_spend != null ? yen(s.ad_spend) : "—"}</td>
                    <td>{s.is_ad && s.ad_spend != null && s.sales != null ? roasStr(s.sales, s.ad_spend) : "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}

        {tab === "period" && (
          <table className="an-table" style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th className="left">期間</th>
                <th>Shopify</th>
                <th>楽天市場</th>
                <th>Amazon</th>
                <th>売上合計</th>
                <th>アクセス</th>
                <th>注文</th>
                <th>転換率</th>
                <th>広告費</th>
                <th>ROAS</th>
                <th className="left">イベント</th>
              </tr>
            </thead>
            <tbody>
              {periodRows.map((b) => {
                const m = sumChannels(b.by_channel, active);
                const we = granularity === "day" && isWeekend(b.start);
                return (
                  <tr key={b.start}>
                    <td className={`left ${we ? "an-weekend" : ""}`}>{b.label !== b.start ? `${b.start}${granularity === "day" ? "" : "〜" + b.end}` : b.start}</td>
                    <td>{channels.shopify ? yen(b.by_channel.shopify.sales) : "—"}</td>
                    <td>{channels.rakuten ? yen(b.by_channel.rakuten.sales) : "—"}</td>
                    <td>{channels.amazon ? yen(b.by_channel.amazon.sales) : "—"}</td>
                    <td className="an-td-strong">{yen(m.sales)}</td>
                    <td>{m.sessions != null ? int(m.sessions) : "—"}</td>
                    <td>{int(m.orders)}</td>
                    <td>{m.sessions ? pctStr(m.orders, m.sessions) : "—"}</td>
                    <td>{yen(m.ad_spend)}</td>
                    <td>{roasStr(m.sales, m.ad_spend)}</td>
                    <td className="an-event-cell">{b.events.map((e) => e.name).join("・")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {tab === "keyword" && (
          <table className="an-table" style={{ minWidth: 800 }}>
            <thead>
              <tr>
                <th className="left">商品</th>
                <th className="left">検索キーワード</th>
                <th>アクセス</th>
                <th>転換率</th>
                <th>市場検索順位</th>
                <th className="left">改善アクション</th>
              </tr>
            </thead>
            <tbody>
              {keywords.map((k, i) => (
                <tr key={`${k.item_name}-${k.keyword}-${i}`}>
                  <td className="left">{k.item_name}</td>
                  <td className="left">{k.keyword}</td>
                  <td>{int(k.access)}</td>
                  <td>{k.cvr != null ? (k.cvr * 100).toFixed(2) + "%" : "—"}</td>
                  <td>{k.rank_label ?? "—"}</td>
                  <td className="left">{k.action ?? "—"}</td>
                </tr>
              ))}
              {keywords.length === 0 && (
                <tr>
                  <td className="left" colSpan={6}>
                    データがありません。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
